import { Component, OnDestroy, OnInit } from '@angular/core';
import { SortEvent } from 'primeng/api';
import { BehaviorSubject, Subject, takeUntil } from 'rxjs';
import { AdminService } from 'src/app/services/admin.service';
import { AppConstants } from 'src/app/shared/app.constants';

@Component({
  selector: 'ness-approvals',
  templateUrl: './approvals.component.html'
})
export class ApprovalsComponent implements OnDestroy, OnInit {
  cols: any[];
  deniedApproval: ServerModels.ENESS_ApplicationUser;
  deniedApprovals: ServerModels.ENESS_ApplicationUser[];
  loadingChangeReq: boolean = false;
  loadingApprovals: boolean = false;
  loadingDenied: boolean;
  openApprovalCommentsForm: boolean;
  pendingApprovals: ServerModels.ENESS_ApplicationUser[];
  pendingChangeRequests: ServerModels.ENESS_XREF_UserProfileChangeRequests[] = [];
  rows: number = AppConstants.defaultTableRows;

  private _eventStream$ = new BehaviorSubject(undefined);
  private _unsubscribe$: Subject<void> = new Subject<void>();

  constructor(private adminService: AdminService) {
    this.loadingChangeReq = false;
    this.loadingApprovals = false;
  }

  ngOnDestroy(): void {
    this._unsubscribe$.next();
    this._unsubscribe$.complete();
  }

  ngOnInit() {
    this.cols = [
      { field: 'CreatedOn', header: 'Request Date' },
      { field: 'LastName', header: 'Last Name' },
      { field: 'FirstName', header: 'First Name' },
      { field: 'PhoneNumber', header: 'Phone' },
      { field: 'ATFFieldDivision', header: 'Field Division' },
      { field: 'FieldDivisionGroups', header: 'Group' },
      { field: 'ENESS_REF_Agency', subfield: 'AgencyAbbreviation', hoverfield: 'AgencyName', header: 'Agency' },
      { field: 'ENESS_REF_UserType', subfield: 'UserTypeDsiaplyName', header: 'User Type' },
      { field: 'Roles', header: 'Role' }
    ];
    this._getChangeRequests();
    this._getPendingApprovals(false);
  }

  public approvalSubmitted(deniedUserData: string): void {
    if (deniedUserData) {
      this.openApprovalCommentsForm = false;
      this.adminService.approveDenyUser(deniedUserData).pipe(takeUntil(this._unsubscribe$)).subscribe({
        next: (result: ServerModels.ENESS_ApplicationUser[]) => { this.pendingApprovals = result },
        error: (err: any) => { }
      });
    }
  }

  public closeCommentsForm(closeForm: boolean): void {
    if (closeForm) {
      this.openApprovalCommentsForm = false;
      this._getPendingApprovals(true);
    }
  }

  public closeCommentSidebar(): void {
    this._getPendingApprovals(true);
  }

  public customSort(event: SortEvent): void {
    let result = null;
    if (event.field == 'Roles') {
      event.data.sort((data1, data2) => {
        const roles1: string = data1['Role'].Name;
        const roles2: string = data2['Role'].Name;

        result = roles1.localeCompare(roles2);
        return (event.order * result);
      });
    } else if (event.field == 'ENESS_REF_UserType') {
      event.data.sort((data1, data2) => {
        const userType1: string = data1.ENESS_REF_UserType.UserTypeDsiaplyName;
        const userType2: string = data2.ENESS_REF_UserType.UserTypeDsiaplyName;

        result = userType1.localeCompare(userType2);
        return (event.order * result);
      });
    } else if (event.field == 'ATFFieldDivision') {
      event.data.sort((data1, data2) => {
        const fd1: string = data1.ATFFieldDivision && data1.ATFFieldDivision.FieldDivisionName;
        const fd2: string = data2.ATFFieldDivision && data2.ATFFieldDivision.FieldDivisionName;

        if (fd1 == null && fd2 != null)
          result = -1;
        else if (fd1 != null && fd2 == null)
          result = 1;
        else if (fd1 == null && fd2 == null)
          result = 0;
        else
          result = fd1.localeCompare(fd2);

        return (event.order * result);
      });
    } else if (event.field == 'FieldDivisionGroups') {
      event.data.sort((data1, data2) => {
        let fdg1: string = data1.FieldDivisionGroups && data1.FieldDivisionGroups.GroupName;
        if (fdg1 == null) {
          fdg1 = data1.ENESS_XREF_ApplicationUserFDGroup && data1.ENESS_XREF_ApplicationUserFDGroup[0] && data1.ENESS_XREF_ApplicationUserFDGroup[0].ENESS_REF_IntelligenceReportATFReferralGroups && data1.ENESS_XREF_ApplicationUserFDGroup[0].ENESS_REF_IntelligenceReportATFReferralGroups.GroupName;
        }
        let fdg2: string = data2.FieldDivisionGroups && data2.FieldDivisionGroups.GroupName;
        if (fdg2 == null) {
          fdg2 = data2.ENESS_XREF_ApplicationUserFDGroup && data2.ENESS_XREF_ApplicationUserFDGroup[0] && data2.ENESS_XREF_ApplicationUserFDGroup[0].ENESS_REF_IntelligenceReportATFReferralGroups && data2.ENESS_XREF_ApplicationUserFDGroup[0].ENESS_REF_IntelligenceReportATFReferralGroups.GroupName;
        }

        if (fdg1 == null && fdg2 != null)
          result = -1;
        else if (fdg1 != null && fdg2 == null)
          result = 1;
        else if (fdg1 == null && fdg2 == null)
          result = 0;
        else
          result = fdg1.localeCompare(fdg2);

        return (event.order * result);
      });
    } else {
      event.data.sort((data1, data2) => {
        let value1 = data1[event.field];
        let value2 = data2[event.field];
        let result = null;

        if (value1 == null && value2 != null)
          result = -1;
        else if (value1 != null && value2 == null)
          result = 1;
        else if (value1 == null && value2 == null)
          result = 0;
        else if (typeof value1 === 'string' && typeof value2 === 'string')
          result = value1.localeCompare(value2);
        else
          result = (value1 < value2) ? -1 : (value1 > value2) ? 1 : 0;

        return (event.order * result);
      });
    }
  }

  public onApproveChange(rowData: ServerModels.ENESS_ApplicationUser): void {
    if (rowData) {
      if (rowData.ENESS_REF_ApprovalHistory.Status == 'Denied') {
        this.deniedApproval = rowData;
        this.openApprovalCommentsForm = true;
      }
      else {
        this.loadingApprovals = true;
        let data = {
            'username': rowData.UserName,
            'status': rowData.ENESS_REF_ApprovalHistory.Status,
            'comment':'' 
        };

        this.adminService.approveDenyUser(data).pipe(takeUntil(this._unsubscribe$)).subscribe({
          next: (result: ServerModels.ENESS_ApplicationUser[]) => {
            this.pendingApprovals = result;
            this.loadingApprovals = false;
          },
          error: (err:any) => {
            this.loadingApprovals = false;
          }
        })
      }
    }
  }

  public onApproveRequest(changeReq: ServerModels.ENESS_XREF_UserProfileChangeRequests): void {
    if (changeReq) {
      this.loadingChangeReq = true;
      this.adminService.approveDenyChangeRequest(changeReq).subscribe((resp: ServerModels.ENESS_ApplicationUser[]) => this.loadingChangeReq = true,
        (err:any) => this.loadingChangeReq = false,
        () => {
          this._eventStream$.next(undefined);
          const index = this.pendingChangeRequests.findIndex(x => x.ChangeRequestID === changeReq.ChangeRequestID);
          this.pendingChangeRequests.splice(index, 1);
          this.loadingChangeReq = false;
        }
      );
    }
  }

  private _getChangeRequests(): void {
    this.loadingChangeReq = true;
    this.adminService.getPendingUserProfileChangeRequests().subscribe((changeRequest: ServerModels.ENESS_XREF_UserProfileChangeRequests[]) => {
      if (changeRequest) {
        changeRequest = changeRequest?.map((item) => {
          if (item.PhoneNumber == null) { return item; }
          item.PhoneNumber = item.PhoneNumber.toLowerCase().replace('(p)', '');
          return item;
        });

        this.pendingChangeRequests = [...changeRequest];
      }
      },
      (err:any) => this.loadingChangeReq = false,
      () => this.loadingChangeReq = false);
  }

  private _getDeniedApprovals() {
    this.loadingDenied = true;
    this.adminService.getDeniedApprovals().pipe(takeUntil(this._unsubscribe$)).subscribe({
      next: (appUser: ServerModels.ENESS_ApplicationUser[]) => this.deniedApprovals = [...appUser],
      complete: () => this.loadingDenied = false,
      error: (err:any) => this.loadingDenied = false
    });
  }

  private _getPendingApprovals(refreshDenied: boolean) {
    this.loadingApprovals = true;
    this.adminService.getPendingApprovals().pipe(takeUntil(this._unsubscribe$)).subscribe({
      next: (appUser: ServerModels.ENESS_ApplicationUser[]) => {
        appUser = appUser.map((item) => {
          if (item.Phone == null) { return item; }
          item.Phone = item.Phone.toLowerCase().replace('(p)', '');
          return item;
        });

        this.pendingApprovals = [...appUser]
      },
      complete: () => {
        this.loadingApprovals = false;
        if (refreshDenied) {
          this._getDeniedApprovals();
        }
      },
      error: (err:any) => this.loadingApprovals = false
    });
  }
}
